import React, {Component} from 'react';
import {CircularProgress, Collapse, LinearProgress, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import {getGoalPercentage} from "./GoalProgressUtils";
import {ExerciseGoalType} from "../../../../core/models/ExerciseGoalType";
import {UnitPrettifier} from "../../../../core/prettifier/UnitPrettifier";
import {HoursMinutesTimeConverter} from "../../../../core/prettifier/HoursMinutesTimeConverter";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

class GoalInlineHint extends Component {

	constructor(props) {
		super(props);
		this.state = {
			expanded: false,
		};
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.goals !== this.props.goals) {
			this.setState({ expanded: false });
		}
	}

	getCompactLabel = (goal) => {
		const typeId = goal.type.id;
		const vals = goal.currentLog?.values;

		switch (typeId) {
			case ExerciseGoalType.WeightAndRepsGoal: {
				const target = `${goal.values[0]} ${UnitPrettifier.weight()} x ${goal.values[1]} reps`;
				if (!vals) return target;
				return `${goal.values[0]} ${UnitPrettifier.weight()} — ${vals[1]} / ${goal.values[1]} reps`;
			}
			case ExerciseGoalType.DistanceAndTimeGoal: {
				const target = `${goal.values[0]} ${UnitPrettifier.distance()} in ${HoursMinutesTimeConverter.convertFloatToString(goal.values[1])}`;
				if (!vals) return target;
				return `${goal.values[0]} ${UnitPrettifier.distance()} — ${HoursMinutesTimeConverter.convertFloatToString(vals[1])} / ${HoursMinutesTimeConverter.convertFloatToString(goal.values[1])}`;
			}
			case ExerciseGoalType.WeightAndTimeGoal: {
				const target = `${goal.values[0]} ${UnitPrettifier.weight()} x ${HoursMinutesTimeConverter.convertFloatToString(goal.values[1])}`;
				if (!vals) return target;
				return `${goal.values[0]} ${UnitPrettifier.weight()} — ${HoursMinutesTimeConverter.convertFloatToString(vals[1])} / ${HoursMinutesTimeConverter.convertFloatToString(goal.values[1])}`;
			}
			default: {
				const current = this.getCurrentValue(goal);
				const target = this.getTargetLabel(goal);
				if (current === null) return target;
				return `${current} / ${target}`;
			}
		}
	}

	getCurrentValue = (goal) => {
		if (!goal.currentLog) return null;
		const typeId = goal.type.id;
		const vals = goal.currentLog.values;

		switch (typeId) {
			case ExerciseGoalType.WeightGoal:
				return `${vals[0]} ${UnitPrettifier.weight()}`;
			case ExerciseGoalType.RepsGoal:
				return `${vals.length === 1 ? vals[0] : vals[1]} reps`;
			case ExerciseGoalType.DistanceGoal:
				return `${vals[0]} ${UnitPrettifier.distance()}`;
			case ExerciseGoalType.TimeGoal:
				return HoursMinutesTimeConverter.convertFloatToString(vals.length === 1 ? vals[0] : vals[1]);
			default:
				return null;
		}
	}

	getTargetLabel = (goal) => {
		const typeId = goal.type.id;
		switch (typeId) {
			case ExerciseGoalType.WeightGoal:
				return `${goal.values[0]} ${UnitPrettifier.weight()}`;
			case ExerciseGoalType.RepsGoal:
				return `${goal.values[0]} reps`;
			case ExerciseGoalType.DistanceGoal:
				return `${goal.values[0]} ${UnitPrettifier.distance()}`;
			case ExerciseGoalType.TimeGoal:
				return HoursMinutesTimeConverter.convertFloatToString(goal.values[0]);
			default:
				return '';
		}
	}

	render() {
		const goals = this.props.goals;
		if (!goals) return null;

		const activeGoals = goals.filter(g => !g.dateCompleted);
		if (activeGoals.length === 0) return null;

		const goalsWithPercentage = activeGoals.map(g => ({
			goal: g,
			percentage: getGoalPercentage(g),
		}));
		goalsWithPercentage.sort((a, b) => b.percentage - a.percentage);
		const closest = goalsWithPercentage[0];

		return <Box sx={{ mb: 0 }}>
			<Box
				onClick={() => this.setState({ expanded: !this.state.expanded })}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					px: 0.5,
					py: 0.5,
					cursor: 'pointer',
				}}
			>
				<Box position="relative" display="inline-flex" sx={{ width: 20, height: 20, flexShrink: 0 }}>
					<CircularProgress
						variant="determinate"
						value={100}
						size={20}
						thickness={3}
						color={closest.percentage >= 90 ? 'success' : 'primary'}
						sx={{ position: 'absolute' }}
					/>
					<CircularProgress
						variant="determinate"
						value={closest.percentage}
						size={20}
						thickness={3}
						sx={{ color: 'secondary.main' }}
					/>
				</Box>

				<Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
					Goal: {this.getCompactLabel(closest.goal)}
				</Typography>

				{activeGoals.length > 1 &&
					<Typography variant="caption" color="text.disabled">
						+{activeGoals.length - 1} more
					</Typography>
				}

				<Typography variant="caption" color="text.disabled">
					{closest.percentage}%
				</Typography>

				{activeGoals.length > 1 && (this.state.expanded
					? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.disabled' }}/>
					: <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }}/>
				)}
			</Box>

			<Collapse in={this.state.expanded}>
				<Box sx={{ pt: 0.5, pb: 0.5 }}>
					{goalsWithPercentage.map(({ goal, percentage }) =>
						<Box key={goal.id} sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							px: 0.5,
							py: 0.5,
						}}>
							<Box sx={{ flexGrow: 1, minWidth: 0 }}>
								<Box display="flex" justifyContent="space-between" alignItems="baseline" gap={0.5}>
									<Typography variant="caption" color="text.secondary">
										{goal.type.name}
									</Typography>
									<Typography variant="caption" color="text.disabled">
										{this.getCompactLabel(goal)} — {percentage}%
									</Typography>
								</Box>
								<LinearProgress
									variant="determinate"
									value={percentage}
									color={percentage >= 90 ? 'success' : 'primary'}
									sx={{ height: 4, borderRadius: 2, mt: 0.25 }}
								/>
							</Box>
						</Box>
					)}
				</Box>
			</Collapse>
		</Box>
	}
}

export default GoalInlineHint;
